import { clamp, wrapAngle, wrapIndex } from '../core/math';
import { speedOf, type KartState } from '../physics/vehicle';
import type { Track } from '../track/generate';
import { maxBrakeDecel, type SpeedProfile } from '../track/speedprofile';
import { scanBrakeDemand } from './autobrake';
import { quantiseBrake, quantiseSteer } from './controls';
import type { TrackQuery } from '../track/lookup';

/**
 * How this driver habitually places its kart and how hard it races.
 *
 * With no style the autopilot drives the centerline exactly as it always has —
 * that is the mode the headless reference driver (drill ghosts, lap validation,
 * `autoDrive`) runs in, and it must stay byte-identical. A style only matters on
 * a Grand Prix grid, where a shared single line would just be a train.
 */
export interface LineStyle {
  /**
   * Habitual offset from the centerline through a corner, as a fraction of the
   * usable half-width. Positive hugs the inside for a tight, pointy line;
   * negative runs wide for a rounder, carry-the-speed line. Kept small — a kart
   * lane is narrow — but enough that two AI cars stop tracing the same rail.
   */
  apexBias: number;
  /**
   * 0..1. Doubles as how readily this driver commits to an overtake and how
   * firmly it defends its line when pressured.
   */
  aggression: number;
}

/** One nearby kart, seen from the autopilot's own kart. */
export interface RivalView {
  /** Signed along-heading gap, m. Positive: rival is ahead. Negative: behind. */
  gap: number;
  /** Rival's position in our body frame, m. Positive is to our left. */
  lateral: number;
  /** Rate the gap is closing, m/s. Positive means we are converging. */
  closing: number;
}

export interface RacecraftView {
  /** Nearest kart ahead within racing range, if any. */
  ahead?: RivalView;
  /** Nearest kart behind within racing range, if any. */
  behind?: RivalView;
}

const NO_STYLE: LineStyle = { apexBias: 0, aggression: 0 };

/** A car ahead closer than this is attackable; also the tow-and-pull-out range. */
const ATTACK_RANGE = 19; // m
/** A car behind closer than this puts us on the defensive. */
const DEFEND_RANGE = 12; // m

/**
 * A competent-but-not-optimal reference driver.
 *
 * It exists so a full lap can be run headlessly: checkpoint counting, lap
 * validation, the analysis pass and the ideal-lap comparison all need a lap to
 * actually happen, and driving one by hand through a browser is neither
 * repeatable nor reviewable.
 *
 * Feed it a *centerline* speed profile (`solveSpeedProfile(track, mu, 0)`), not
 * the width-aware one the player is scored against — it drives close to the
 * centerline, so aiming at speeds that assume a proper racing line puts it in
 * the grass. The `style`/`rivals` racecraft below only ever shifts the *line*
 * laterally by a bounded amount at those same conservative speeds; it never
 * raises the target speed, so the grass-avoidance guarantee still holds.
 */
export class Autopilot {
  private readonly brakeCap = maxBrakeDecel();
  private readonly style: LineStyle;

  constructor(
    private readonly track: Track,
    private readonly profile: SpeedProfile,
    private readonly query: TrackQuery,
    /** Fraction of the profile to aim for. Margin for an imperfect line. */
    private readonly pace = 0.7,
    style?: LineStyle,
  ) {
    this.style = style ?? NO_STYLE;
  }

  /**
   * Quantised steer/brake bytes, the same form a human's input is recorded in.
   *
   * `rivals` is the racecraft input: pass it on a Grand Prix grid so the driver
   * can attack a car ahead and cover its line from a car behind. Omit it (every
   * headless caller does) and the driver behaves exactly as the pure centerline
   * pursuit always has.
   */
  sample(kart: KartState, rivals?: RacecraftView): { steerByte: number; brakeByte: number } {
    const n = this.track.center.length;
    const ds = this.track.spacing;
    const speed = speedOf(kart);
    const here = this.query.sample(kart.x, kart.y);

    // Room to move without putting a wheel on the kerb — the lateral budget for
    // every line choice below.
    const room = Math.max(0, this.track.width / 2 - 2.1);

    // What the road is about to do. Sensed further ahead than the steering
    // lookahead so the line is already chosen before turn-in. Signed: positive
    // curvature is a left-hander, so its inside is to the left (+normal).
    const senseIdx = wrapIndex(here.index + Math.round(clamp(9 + speed * 0.5, 9, 32) / ds), n);
    const kSense = this.track.curvature[senseIdx];
    const cornerDir = kSense > 0 ? 1 : kSense < 0 ? -1 : 0;
    const cornerStrength = clamp(Math.abs(kSense) * 55, 0, 1); // ~1 at a real corner

    // --- pick a lateral offset from the centerline (m, + = left) --------------
    // Baseline: this driver's habitual line, only meaningful in a corner.
    let offset = this.style.apexBias * room * cornerDir * cornerStrength;
    // Scales `this.pace` inside the braking scan: >1 brakes later (a committed
    // dive), <1 brakes earlier (securing a defensive line).
    let braceScale = 1;

    const ahead = rivals?.ahead;
    const behind = rivals?.behind;

    if (ahead && ahead.gap > 0 && ahead.gap < ATTACK_RANGE && this.style.aggression > 0) {
      // --- attacking --------------------------------------------------------
      const near = 1 - ahead.gap / ATTACK_RANGE;
      if (cornerStrength > 0.3) {
        // A corner is coming. Take the inside unless the rival already has it,
        // in which case set up for the switchback off the outside instead. One
        // decisive move, its size and brake commitment scaled by aggression.
        const rivalHasInside =
          Math.sign(ahead.lateral) === cornerDir && Math.abs(ahead.lateral) > room * 0.3;
        const moveDir = rivalHasInside ? -cornerDir : cornerDir;
        offset = moveDir * room * (0.5 + 0.35 * this.style.aggression);
        if (!rivalHasInside) braceScale = 1 + 0.07 * this.style.aggression * near;
      } else {
        // On a straight: sit in the tow, then pull to the side with more room to
        // complete the pass. `near` keeps the pull-out gradual from distance.
        const pullDir = ahead.lateral >= 0 ? -1 : 1;
        offset = pullDir * room * 0.75 * near;
      }
    } else if (behind && behind.gap < 0 && -behind.gap < DEFEND_RANGE && behind.closing > 0) {
      // --- defending --------------------------------------------------------
      // Exactly one move: it is legal to pick your line, not to weave.
      const near = 1 - -behind.gap / DEFEND_RANGE;
      if (cornerStrength > 0.25) {
        offset = cornerDir * room * (0.45 + 0.35 * this.style.aggression);
        braceScale = 1 - 0.05 * this.style.aggression * near; // brake early, take the point
      } else {
        // Straight: move across to sit in front of the follower and break the
        // tow, capped so it stays a single lane change rather than a block.
        offset = clamp(behind.lateral, -room * 0.55, room * 0.55) * near;
      }
    }

    offset = clamp(offset, -room, room);

    // --- steering: pure pursuit toward the offset line ---------------------
    const lookahead = clamp(3 + speed * 0.38, 4, 18);
    const aheadIdx = wrapIndex(here.index + Math.round(lookahead / ds), n);
    const c = this.track.center[aheadIdx];
    const nrm = this.track.normal[aheadIdx];
    const target = { x: c.x + nrm.x * offset, y: c.y + nrm.y * offset };

    const desired = Math.atan2(target.y - kart.y, target.x - kart.x);
    // Steer against where the kart will be pointing shortly, not where it points
    // now. Without this lead term the bang-bang output overshoots into a limit
    // cycle and the kart weaves itself off the road.
    const predicted = kart.heading + kart.yawRate * 0.18;
    const error = wrapAngle(desired - predicted);

    // Proportional now that the trace carries analogue values — the bot no
    // longer has to dither a binary key to hold a partial steering angle.
    const steer = clamp(error * 3.2, -1, 1);

    // --- braking: is there room left to slow down? -------------------------
    // The scan itself lives in autobrake.ts, shared with the player's automatic
    // braking. These numbers are this driver's and must not drift: drill
    // ghosts, lap validation and `autoDrive` all expect its traces to stay
    // byte-identical.
    const pace = clamp(this.pace * braceScale, 0.4, 1);
    const brake = scanBrakeDemand(this.track, this.profile, here.index, speed, {
      scan: 90,
      pace,
      brakeCap: this.brakeCap,
      floor: 0.4,
      span: 0.5,
    });

    return { steerByte: quantiseSteer(steer), brakeByte: quantiseBrake(brake) };
  }
}
